//libraries
import React, { memo, useContext } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useLocation } from 'react-router-dom';

//context
import { SubNavContext } from 'shared/components/SubNav';
import { useAuthModalWrapperContext } from 'shared/components/AuthModal/AuthModalWrapperContext';
import { useToastContext } from 'shared/components/Toast';

//constants
import { TEXTS } from 'shared/components/Header/DesktopHeaderV2/components/HeaderActions/components/Auth/constants';
import { AUTH_URI, MY_ORDERS, MY_ORDERS_V2, WISHLIST } from 'shared/constants/uri';

//styles
import { Logout, MenuItemWrapper, Greeting, Info, Notification } from './styles';

// helpers
import { handleLogoutClick } from 'shared/helpers/logout';

// icons
import { useStoreIcon, IconName } from 'shared/icons';
import NFA from 'shared/helpers/analytics/NFAnalytics';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useUserData } from 'shared/components/UserData/hooks';

//def
interface ILoggedInProps {
  userName: string;
  mobile: string;
  email: string;
  hideEmail: boolean;
  shouldVerifyMobile: boolean;
  shouldVerifyEmail: boolean;
  onOpenSavedPayments?: () => void;
}

function LoggedIn({
  userName,
  mobile,
  email,
  hideEmail,
  shouldVerifyMobile,
  shouldVerifyEmail,
  onOpenSavedPayments,
}: ILoggedInProps) {
  // Get icon components from unified system
  const OrdersIcon = useStoreIcon(IconName.BOX);
  const SavedPaymentsIcon = useStoreIcon(IconName.SAVED_PAYMENTS);

  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  const { doHide } = useContext(SubNavContext);
  const { showAuthModal } = useAuthModalWrapperContext();
  const { showToast } = useToastContext();
  const { clearUserData } = useUserData();
  const postOrderUrlV2Enabled = useRemoteConfig('postOrderUrlV2Enabled');
  const shouldRedirectHomeAfterLogout = pathname.replace(/\/$/, '') === WISHLIST;

  const goToMyOrder = () => {
    doHide();
    window.location.href = postOrderUrlV2Enabled ? MY_ORDERS_V2 : MY_ORDERS;
  };

  const logout = () => {
    NFA.reset();
    doHide();
    void handleLogoutClick(showToast, clearUserData, {
      successMessage: TEXTS.USER_LOGGEDOUT,
      errorMessage: TEXTS.API_ERROR_MESSAGE,
      redirectPath: shouldRedirectHomeAfterLogout ? '/' : undefined,
    });
  };

  const clickHandler = (requiredURI: string) => {
    params.set(AUTH_URI.PARAM_KEY, requiredURI);
    const url = pathname + '?' + params.toString();
    doHide();

    showAuthModal(url);
  };

  return (
    <MenuItemWrapper>
      <Greeting data-cs-mask>{TEXTS.HI_USER(userName)}</Greeting>
      {isEmpty(mobile) === false && <Info data-cs-mask>{mobile}</Info>}
      {isEmpty(email) === false && hideEmail === false && <Info data-cs-mask>{email}</Info>}

      {shouldVerifyMobile === true && (
        <Notification onClick={() => clickHandler(AUTH_URI.MOBILE_NUMBER_VERIFICATION)}>
          {TEXTS.VERIFY_MOBILE_LABEL}
        </Notification>
      )}

      {shouldVerifyEmail === true && (
        <Notification onClick={() => clickHandler(AUTH_URI.MERGE_ACCOUNTS)}>
          {TEXTS.VERIFY_EMAIL_LABEL}
        </Notification>
      )}

      <button onClick={goToMyOrder} className="sub-menu__menu-item border-bottom border-top">
        {TEXTS.ORDERS}
        <OrdersIcon />
      </button>

      {onOpenSavedPayments && (
        <button
          onClick={() => {
            doHide();
            onOpenSavedPayments();
          }}
          className="sub-menu__menu-item"
        >
          Saved Payments
          <SavedPaymentsIcon aria-hidden="true" />
        </button>
      )}

      <Logout onClick={logout}>{TEXTS.LOGOUT}</Logout>
    </MenuItemWrapper>
  );
}

export default memo(LoggedIn);
